import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../api/axios';
import { Download, FileText, Users, Star, TrendingUp, MessageSquare, Calendar, Trash2, ChevronRight, Quote, BarChart2, Activity } from 'lucide-react';

const LABELS = ['', 'Çok Kötü', 'Kötü', 'İdare Eder', 'İyi', 'Çok İyi'];
const COLORS = ['', '#ef4444', '#f97316', '#eab308', '#22c55e', '#10b981'];

export default function AdminSurveyResults() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [confirmModal, setConfirmModal] = React.useState({ open: false, action: null, title: '', message: '' });
  const showConfirm = (title, message, action) => setConfirmModal({ open: true, title, message, action });
  const closeConfirm = () => setConfirmModal({ open: false, action: null, title: '', message: '' });
  
  const [data, setData] = useState(null);
  const [responses, setResponses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const query = params.toString() ? '?' + params.toString() : '';
    const [statsRes, respRes] = await Promise.all([
      api.get('/surveys/egitim-degerlendirme/stats' + query),
      api.get('/surveys/egitim-degerlendirme/responses' + query)
    ]);
    setData(statsRes.data);
    setResponses(respRes.data);
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => { loadData(); }, [loadData]);

  const deleteResponse = (id) => {
    showConfirm('Yanıtı Sil', 'Bu anket yanıtını silmek istediğinize emin misiniz?', async () => {
      await api.delete('/surveys/responses/' + id);
      loadData();
      closeConfirm();
    });
  };

  const exportCSV = () => {
    if (!responses) return;
    const ratingQs = responses.questions.filter(q => q.question_type === 'rating');
    const textQs = responses.questions.filter(q => q.question_type === 'textarea');
    const headers = ['Ad Soyad', 'Tarih', ...ratingQs.map(q => q.label), ...textQs.map(q => q.label)];
    const rows = responses.responses.map(r => [
      r.respondent_name || 'Anonim',
      new Date(r.submitted_at).toLocaleDateString('tr-TR'),
      ...ratingQs.map(q => LABELS[r.answers[q.id]] || '-'),
      ...textQs.map(q => (r.answers[q.id] || '').replace(/,/g, ';'))
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'anket-sonuclari.csv'; a.click();
  };

  const exportPDF = () => {
    if (!data) return;
    setGeneratingPdf(true);

    const dateRange = startDate && endDate
      ? `${new Date(startDate).toLocaleDateString('tr-TR')} - ${new Date(endDate).toLocaleDateString('tr-TR')}`
      : startDate ? `${new Date(startDate).toLocaleDateString('tr-TR')} sonrası`
      : endDate ? `${new Date(endDate).toLocaleDateString('tr-TR')} öncesi`
      : 'Tüm Zamanlar';

    const ratingStats = data.stats.filter(q => q.question_type === 'rating');
    const overallAvgVal = ratingStats.length > 0
      ? (ratingStats.reduce((a, b) => a + parseFloat(b.average || 0), 0) / ratingStats.length).toFixed(2)
      : 0;
    const sectionsList = [...new Set(data.stats.map(q => q.section))];

    const sectionHTML = sectionsList.map(sec => {
      const qs = data.stats.filter(q => q.section === sec && q.question_type === 'rating');
      const avg = qs.length > 0
        ? (qs.reduce((a, b) => a + parseFloat(b.average || 0), 0) / qs.length).toFixed(2)
        : 0;
      const rows = qs.map(q => `
        <tr>
          <td style="padding:5px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;color:#374151;width:70%;">${q.label}</td>
          <td style="padding:5px 10px;border-bottom:1px solid #f0f0f0;text-align:center;font-size:12px;color:#f59e0b;white-space:nowrap;width:18%;">${"★".repeat(Math.round(parseFloat(q.average)))}${"☆".repeat(5 - Math.round(parseFloat(q.average)))}</td>
          <td style="padding:5px 10px;border-bottom:1px solid #f0f0f0;text-align:center;font-weight:800;font-size:13px;color:#6366f1;white-space:nowrap;width:12%;">${q.average}</td>
        </tr>`).join('');
      return `
        <div style="margin-bottom:12px;page-break-inside:avoid;">
          <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;">
            <tr>
              <td style="background:#0f172a;color:white;padding:7px 12px;font-weight:700;font-size:11px;border-radius:6px 0 0 0;">${sec}</td>
              <td style="background:#0f172a;color:white;padding:7px 12px;font-size:11px;text-align:right;border-radius:0 6px 0 0;">Ort: <strong>${avg}</strong></td>
            </tr>
          </table>
          <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #e5e7eb;border-top:none;">
            <thead>
              <tr style="background:#f8faff;">
                <th style="padding:5px 10px;text-align:left;font-size:10px;color:#9ca3af;font-weight:700;width:70%;">SORU</th>
                <th style="padding:5px 10px;text-align:center;font-size:10px;color:#9ca3af;font-weight:700;width:18%;">YILDIZ</th>
                <th style="padding:5px 10px;text-align:center;font-size:10px;color:#9ca3af;font-weight:700;width:12%;">ORT.</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    }).join('');

    const textQs = data.stats.filter(q => q.question_type === 'textarea');
    const textHTML = textQs.length > 0 ? `
      <div style="margin-top:14px;page-break-inside:avoid;">
        <div style="font-weight:700;font-size:13px;color:#1e293b;margin-bottom:10px;padding-bottom:5px;border-bottom:2px solid #0f172a;">Açık Uçlu Yanıtlar</div>
        ${textQs.map(q => `
          <div style="margin-bottom:10px;page-break-inside:avoid;">
            <div style="font-weight:700;font-size:11px;padding:6px 10px;background:#f8faff;border-radius:4px;margin-bottom:5px;">${q.label}</div>
            ${q.texts && q.texts.length > 0
              ? q.texts.map(t => `<div style="padding:5px 10px;border-left:3px solid #0f172a;margin-bottom:4px;font-size:11px;color:#374151;line-height:1.5;">${t}</div>`).join('')
              : '<div style="font-size:11px;color:#9ca3af;padding:5px 10px;">Cevap yok</div>'}
          </div>`).join('')}
      </div>` : '';

    const html = `<!DOCTYPE html>
<html lang="tr">
<head><meta charset="UTF-8"><title>Anket Raporu</title>
<style>
  @page { size: A4 portrait; margin: 15mm 14mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; font-family: Arial, sans-serif; }
  body { font-size: 11px; line-height: 1.4; color: #111; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>
<table width="100%" cellpadding="0" cellspacing="0" style="background:#0f172a;border-radius:8px;margin-bottom:12px;">
  <tr>
    <td style="padding:14px 18px;">
      <div style="font-size:9px;color:rgba(255,255,255,0.6);text-transform:uppercase;letter-spacing:1px;margin-bottom:3px;">EGITIM DEGERLENDIRME RAPORU</div>
      <div style="font-size:18px;font-weight:800;color:white;margin-bottom:4px;">Eğitim ve Akademi Platformu</div>
      <div style="font-size:10px;color:rgba(255,255,255,0.85);">Donem: ${dateRange} &nbsp;|&nbsp; Katilimci: <strong>${data.total}</strong></div>
    </td>
  </tr>
</table>
${sectionHTML}
${textHTML}
</body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.print(); setGeneratingPdf(false); }, 600);
  };

  // Veri İşleme
  const sections = data ? [...new Set(data.stats.map(q => q.section))] : [];
  const ratingStats = data ? data.stats.filter(q => q.question_type === 'rating') : [];
  const overallAvg = ratingStats.length > 0 ? parseFloat((ratingStats.reduce((a, b) => a + parseFloat(b.average || 0), 0) / ratingStats.length).toFixed(2)) : 0;
  
  const sectionAvgs = sections.map(sec => {
    const qs = ratingStats.filter(q => q.section === sec);
    const avg = qs.length > 0 ? (qs.reduce((a, b) => a + parseFloat(b.average || 0), 0) / qs.length).toFixed(2) : 0;
    return { section: sec, avg: parseFloat(avg) };
  });

  // Halka Grafik Geometrisi
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (overallAvg / 5) * circumference;

  return (
    <AdminLayout>
      {/* HEADER: Floating Action Island */}
      <div className="command-header">
        <div>
          <h2 className="command-title">Anket Analizi</h2>
          <p className="command-subtitle">Eğitim değerlendirme sonuçları ve geri bildirimler.</p>
        </div>
        
        <div className="action-island">
          <div className="date-filter-group">
            <Calendar size={14} className="text-muted" />
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="stealth-input" />
            <span className="text-muted" style={{fontSize: 12}}>→</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="stealth-input" />
          </div>
          
          <div className="divider-v" />
          
          <button onClick={exportCSV} className="island-btn">
            <Download size={14} /> <span className="hide-mobile">CSV</span>
          </button>
          <button onClick={exportPDF} disabled={generatingPdf} className="island-btn primary">
            <FileText size={14} /> <span className="hide-mobile">{generatingPdf ? '...' : 'Rapor'}</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <Activity className="spinner" size={32} />
          <p>Veriler derleniyor...</p>
        </div>
      ) : data.total === 0 ? (
        <div className="empty-state-bento">
          <div className="empty-icon">📭</div>
          <h3>Yanıt Bulunamadı</h3>
          <p>Seçili tarih aralığında herhangi bir değerlendirme kaydı yok.</p>
        </div>
      ) : (
        <div className="bento-layout">
          
          {/* 1. BENTO: Overall Score (Hero Widget) */}
          <div className="bento-box hero-box">
            <div className="hero-content">
              <h3 className="bento-title-light">Genel Memnuniyet Skoru</h3>
              <div className="gauge-container">
                <svg width="160" height="160" className="gauge-svg">
                  <circle cx="80" cy="80" r={radius} className="gauge-bg" />
                  <circle cx="80" cy="80" r={radius} className="gauge-progress"
                    style={{ strokeDasharray: circumference, strokeDashoffset: strokeDashoffset, stroke: overallAvg >= 4 ? '#10b981' : overallAvg >= 3 ? '#f59e0b' : '#ef4444' }} />
                </svg>
                <div className="gauge-center">
                  <span className="gauge-value">{overallAvg.toFixed(1)}</span>
                  <span className="gauge-max">/ 5</span>
                </div>
              </div>
              <div className="hero-stats">
                <div className="hero-stat-item">
                  <span className="stat-label">Toplam Katılım</span>
                  <span className="stat-val">{data.total} <Users size={12}/></span>
                </div>
                <div className="hero-stat-item">
                  <span className="stat-label">Performans</span>
                  <span className="stat-val" style={{color: overallAvg >= 4 ? '#10b981' : '#f59e0b'}}>{overallAvg >= 4 ? 'Mükemmel' : 'Geliştirilmeli'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 2. BENTO: Section Breakdown */}
          <div className="bento-box breakdown-box">
            <div className="bento-header">
              <h3 className="bento-title">Kategori Bazlı Analiz</h3>
              <BarChart2 size={16} className="text-muted" />
            </div>
            <div className="breakdown-list">
              {sectionAvgs.map(({ section, avg }) => (
                <div key={section} className="breakdown-item">
                  <div className="d-flex justify-between align-center mb-1">
                    <span className="bd-label">{section}</span>
                    <span className="bd-score font-bold">{avg}</span>
                  </div>
                  <div className="bd-track">
                    <div className="bd-fill" style={{ width: `${(avg / 5) * 100}%`, background: avg >= 4 ? '#10b981' : avg >= 3 ? '#6366f1' : '#ef4444' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 3. BENTO: Detailed Questions Matrix */}
          <div className="bento-box matrix-box">
            <div className="bento-header">
              <h3 className="bento-title">Soru Matrisi</h3>
              <Star size={16} className="text-muted" />
            </div>
            <div className="matrix-list">
              {ratingStats.map(q => {
                const avg = parseFloat(q.average || 0);
                return (
                  <div key={q.id} className="matrix-item">
                    <div className="matrix-info">
                      <div className="matrix-label">{q.label}</div>
                      <div className="matrix-section-tag">{q.section}</div>
                    </div>
                    <div className="matrix-score-area">
                      <div className="matrix-score-val font-bold">{avg.toFixed(1)}</div>
                      <div className="matrix-stars">
                        {[1,2,3,4,5].map(s => (
                          <div key={s} className={`matrix-star-segment ${s <= Math.round(avg) ? 'filled' : ''}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 4. BENTO: Textual Feedback Stream (Gelen Yorumlar) */}
          <div className="bento-box feedback-box">
            <div className="bento-header">
              <h3 className="bento-title">Açık Uçlu Geri Bildirimler</h3>
              <MessageSquare size={16} className="text-muted" />
            </div>
            <div className="feedback-stream">
              {data.stats.filter(q => q.question_type === 'textarea').map(q => (
                <div key={q.id} className="feedback-group">
                  <div className="feedback-q-label"><Quote size={12}/> {q.label}</div>
                  {q.texts && q.texts.length > 0 ? (
                    <div className="feedback-bubbles">
                      {q.texts.map((text, i) => (
                        <div key={i} className="f-bubble">{text}</div>
                      ))}
                    </div>
                  ) : (
                    <div className="f-empty">Yanıt verilmemiş.</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 5. BENTO: Respondents Table */}
          {responses && (
            <div className="bento-box table-box">
              <div className="bento-header border-b pb-3 mb-0">
                <h3 className="bento-title">Katılımcı Listesi</h3>
                <span className="badge-dark">{responses.responses.length} Kayıt</span>
              </div>
              <div className="table-responsive">
                <table className="bento-table">
                  <thead>
                    <tr>
                      <th>Katılımcı Bilgisi</th>
                      <th>Tamamlama Tarihi</th>
                      <th className="text-center">Ortalama Puan</th>
                      {isAdmin && <th className="text-center">İşlem</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {responses.responses.map((r, i) => {
                      const ratingQs = responses.questions.filter(q => q.question_type === 'rating');
                      const avg = ratingQs.length > 0 ? (ratingQs.reduce((a, q) => a + (parseInt(r.answers[q.id]) || 0), 0) / ratingQs.length).toFixed(1) : '-';
                      return (
                        <tr key={i}>
                          <td>
                            <div className="user-cell">
                              <div className="user-avatar-mini">{(r.respondent_name || 'A').charAt(0).toUpperCase()}</div>
                              <span className="font-bold">{r.respondent_name || 'Anonim Katılımcı'}</span>
                            </div>
                          </td>
                          <td className="text-muted" style={{fontSize: 12}}>
                            {new Date(r.submitted_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="text-center">
                            <span className="score-pill">★ {avg}</span>
                          </td>
                          {isAdmin && (
                            <td className="text-center">
                              <button onClick={() => deleteResponse(r.id)} className="btn-icon-del">
                                <Trash2 size={14} />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => { confirmModal.action && confirmModal.action(); }}
        onCancel={closeConfirm}
        confirmText="Evet, Sil"
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        /* Sıfırlama ve Temel Ayarlar */
        * { box-sizing: border-box; font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; }
        
        /* Utility Sınıfları */
        .d-flex { display: flex; }
        .flex-col { flex-direction: column; }
        .align-center { align-items: center; }
        .align-end { align-items: flex-end; }
        .align-start { align-items: flex-start; }
        .justify-between { justify-content: space-between; }
        .justify-center { justify-content: center; }
        .flex-wrap { flex-wrap: wrap; }
        .flex-1 { flex: 1; }
        .gap-1 { gap: 4px; }
        .gap-2 { gap: 8px; }
        .gap-3 { gap: 12px; }
        .font-bold { font-weight: 600; }
        .text-dark { color: #0f172a; }
        .text-muted { color: #64748b; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .pb-3 { padding-bottom: 12px; }
        .mb-0 { margin-bottom: 0 !important; }
        .mb-1 { margin-bottom: 4px; }
        .mb-2 { margin-bottom: 8px; }
        .mb-4 { margin-bottom: 24px; }
        .ml-1 { margin-left: 4px; }
        .border-b { border-bottom: 1px solid #e2e8f0; }
        
        /* Command Header (Island Style) */
        .command-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; flex-wrap: wrap; gap: 16px; }
        .command-title { font-size: 24px; font-weight: 800; color: #0f172a; margin: 0 0 4px 0; letter-spacing: -0.5px; }
        .command-subtitle { font-size: 13px; color: #64748b; margin: 0; }
        
        .action-island { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 100px; padding: 6px; display: flex; align-items: center; gap: 8px; box-shadow: 0 4px 12px rgba(15,23,42,0.03); flex-wrap: wrap;}
        .date-filter-group { display: flex; align-items: center; gap: 8px; padding: 0 12px; }
        .stealth-input { border: none; background: transparent; font-size: 13px; font-weight: 500; color: #334155; outline: none; cursor: pointer; font-family: 'Inter', sans-serif;}
        .divider-v { width: 1px; height: 20px; background: #e2e8f0; }
        .island-btn { background: transparent; border: none; padding: 8px 16px; border-radius: 100px; cursor: pointer; font-size: 13px; font-weight: 600; color: #475569; display: flex; align-items: center; gap: 6px; transition: all 0.2s; }
        .island-btn:hover { background: #f1f5f9; color: #0f172a; }
        .island-btn.primary { background: #0f172a; color: white; }
        .island-btn.primary:hover:not(:disabled) { background: #1e293b; box-shadow: 0 4px 12px rgba(15,23,42,0.15); transform: translateY(-1px); }
        .island-btn.primary:disabled { opacity: 0.6; cursor: not-allowed; }
        @media (max-width: 768px) { .hide-mobile { display: none; } .action-island { border-radius: 16px; padding: 12px; } }

        /* Yükleniyor / Boş Durum */
        .loading-state { text-align: center; padding: 60px; color: #64748b; font-weight: 500; }
        .spinner { animation: spin 1s linear infinite; margin-bottom: 12px; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
        .empty-state-bento { background: white; border-radius: 24px; padding: 60px 20px; text-align: center; border: 1px dashed #cbd5e1; }
        .empty-icon { font-size: 48px; margin-bottom: 16px; opacity: 0.5; }

        /* BENTO LAYOUT GRID */
        .bento-layout {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          grid-auto-rows: minmax(100px, auto);
          gap: 20px;
        }
        
        .bento-box { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 24px; padding: 24px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02); display: flex; flex-direction: column;}
        .bento-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
        .bento-title { font-size: 15px; font-weight: 700; color: #0f172a; margin: 0; }
        
        /* 1. Hero Box (Koyu Renk, Radial Progress) */
        .hero-box { grid-column: span 4; background: #0f172a; color: white; border: none; box-shadow: 0 10px 30px rgba(15,23,42,0.15); position: relative; overflow: hidden; justify-content: center;}
        .hero-box::before { content: ''; position: absolute; top: -50%; left: -50%; width: 200%; height: 200%; background: radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 60%); pointer-events: none; }
        .bento-title-light { font-size: 14px; font-weight: 500; color: #94a3b8; margin: 0 0 24px 0; text-align: center; letter-spacing: 0.5px; text-transform: uppercase;}
        
        .gauge-container { position: relative; display: flex; justify-content: center; margin-bottom: 32px; }
        .gauge-svg { transform: rotate(-90deg); overflow: visible; }
        .gauge-bg { fill: none; stroke: rgba(255,255,255,0.05); stroke-width: 14; }
        .gauge-progress { fill: none; stroke-width: 14; stroke-linecap: round; transition: stroke-dashoffset 1.5s cubic-bezier(0.4, 0, 0.2, 1); filter: drop-shadow(0 0 8px currentColor); }
        .gauge-center { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center; }
        .gauge-value { display: block; font-size: 42px; font-weight: 800; line-height: 1; letter-spacing: -1px; }
        .gauge-max { display: block; font-size: 13px; color: #64748b; font-weight: 600; margin-top: 4px; }
        
        .hero-stats { display: flex; gap: 12px; }
        .hero-stat-item { flex: 1; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 16px; padding: 12px; display: flex; flex-direction: column; justify-content: center; }
        .stat-label { font-size: 11px; color: #94a3b8; margin-bottom: 4px; font-weight: 500; }
        .stat-val { font-size: 15px; font-weight: 700; display: flex; align-items: center; justify-content: space-between; }

        /* 2. Breakdown Box */
        .breakdown-box { grid-column: span 8; }
        .breakdown-list { display: flex; flex-direction: column; gap: 16px; flex: 1; justify-content: center;}
        .breakdown-item { display: flex; flex-direction: column; }
        .bd-label { font-size: 13px; font-weight: 600; color: #334155; }
        .bd-score { font-size: 14px; color: #0f172a; }
        .bd-track { height: 8px; background: #f1f5f9; border-radius: 4px; overflow: hidden; margin-top: 6px; }
        .bd-fill { height: 100%; border-radius: 4px; transition: width 1s ease-out; }

        /* 3. Matrix Box */
        .matrix-box { grid-column: span 7; }
        .matrix-list { display: flex; flex-direction: column; gap: 12px; max-height: 400px; overflow-y: auto; padding-right: 8px; }
        .matrix-item { display: flex; justify-content: space-between; align-items: center; padding: 16px; background: #f8fafc; border-radius: 16px; border: 1px solid #f1f5f9; gap: 16px; transition: transform 0.2s;}
        .matrix-item:hover { transform: translateX(4px); background: #f1f5f9; }
        .matrix-info { flex: 1; min-width: 0; }
        .matrix-label { font-size: 13px; font-weight: 600; color: #1e293b; margin-bottom: 4px; line-height: 1.4; }
        .matrix-section-tag { display: inline-block; font-size: 10px; font-weight: 700; color: #6366f1; background: #eef2ff; padding: 2px 8px; border-radius: 100px; }
        .matrix-score-area { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; }
        .matrix-score-val { font-size: 20px; line-height: 1; color: #0f172a; margin-bottom: 6px; }
        .matrix-stars { display: flex; gap: 2px; }
        .matrix-star-segment { width: 12px; height: 4px; background: #e2e8f0; border-radius: 2px; }
        .matrix-star-segment.filled { background: #f59e0b; }

        /* 4. Feedback Box (Chat Bubbles) */
        .feedback-box { grid-column: span 5; }
        .feedback-stream { display: flex; flex-direction: column; gap: 20px; max-height: 400px; overflow-y: auto; padding-right: 8px; }
        .feedback-q-label { font-size: 12px; font-weight: 700; color: #475569; display: flex; align-items: center; gap: 6px; margin-bottom: 12px; }
        .feedback-bubbles { display: flex; flex-direction: column; gap: 8px; }
        .f-bubble { background: #f1f5f9; padding: 12px 16px; border-radius: 2px 16px 16px 16px; font-size: 13px; color: #334155; line-height: 1.5; border: 1px solid #e2e8f0; }
        .f-empty { font-size: 12px; color: #94a3b8; font-style: italic; }

        /* 5. Table Box */
        .table-box { grid-column: span 12; padding: 0; overflow: hidden; }
        .badge-dark { background: #0f172a; color: white; font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 100px; }
        .table-box .bento-header { padding: 24px 24px 0 24px; }
        
        .table-responsive { overflow-x: auto; width: 100%; }
        .bento-table { width: 100%; border-collapse: collapse; min-width: 600px; }
        .bento-table th { padding: 16px 24px; text-align: left; font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; }
        .bento-table td { padding: 16px 24px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; transition: background 0.2s; }
        .bento-table tr:hover td { background: #f8fafc; }
        .bento-table tr:last-child td { border-bottom: none; }
        
        .user-cell { display: flex; align-items: center; gap: 12px; }
        .user-avatar-mini { width: 32px; height: 32px; border-radius: 10px; background: #eef2ff; color: #4f46e5; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; border: 1px solid #e0e7ff;}
        .score-pill { background: #f8fafc; border: 1px solid #e2e8f0; padding: 4px 12px; border-radius: 100px; font-size: 13px; font-weight: 700; color: #0f172a; display: inline-block; }
        
        .btn-icon-del { background: #fef2f2; color: #ef4444; border: 1px solid #fecaca; border-radius: 8px; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; margin: 0 auto; }
        .btn-icon-del:hover { background: #ef4444; color: white; }

        /* Custom Scrollbar */
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

        /* Responsive Grid Adjustments */
        @media (max-width: 1024px) {
          .hero-box { grid-column: span 12; }
          .breakdown-box { grid-column: span 12; }
          .matrix-box { grid-column: span 12; }
          .feedback-box { grid-column: span 12; }
        }
      `}</style>
    </AdminLayout>
  );
}
